import React, { useCallback, useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import betslip, { Betslip } from '@gin-betting/betslip';
import core, { App, GQL, View } from '@gin-betting/core';
import restrictionsModule from '@gin-betting/restrictions';
import { includes } from 'lodash';
import { createSelector } from 'reselect';
import type { Arg3 } from 'tsargs';

import { useAsyncModule } from 'async-modules';
import { usePollingMinStake } from 'hooks';
import ui from 'modules/ui';
import type { State } from 'types';
import { Modules } from 'types';
import { bindTrailingArgs } from 'utils';
import messages from '../../messages';
import { RestrictionsContext } from '../../restrictions';
import { makeSelectIsFreebet } from '../../selectors';
import { getRestrictionBlackList, getRestrictionMessage } from '../../utils';
import { ValidOddProps } from './useValidOddProps';

const {
    entities: {
        selectors: { selectCachedBet },
    },
    utils: { checkIsBetslipOddActive },
} = core;

const {
    utils: { getErrorMsg: getErrorMsgByResponse, convertViewMaxStake },
    actions: { deleteBetslipOdd },
    selectors: { selectIsBetslipOddConflict },
    constants: { BETSLIP_ODD_STATUSES_TO_PLACE_BET },
} = betslip;

const {
    selectors: { selectOddFormat },
} = ui;

const {
    selectors: { selectFilteredRestrictionsByOddId },
    utils: { normalizeRestriction },
} = restrictionsModule;

interface Input extends ValidOddProps {
    isLast: boolean;
}

type Output = {
    clickOddIconHandler: VoidFn;
    oddFormat: ReturnType<typeof selectOddFormat>;
    isLocked: boolean;
    marketName: string;
    oddName: string;
    oddValue: number;
    errorMsgs: Array<string | string[]> | React.ReactNode[];
    hasError: boolean;
};

const useOdd = ({
    isLast,
    betslipOdd,
    betslipBet,
    market,
    match,
    odd,
}: Input): Output => {
    const {
        id: betslipOddId,
        singleStatus: betslipStatus,
        fixedCoef,
        betId,
    } = betslipOdd;

    const isEditable = includes(
        BETSLIP_ODD_STATUSES_TO_PLACE_BET,
        betslipStatus
    );

    const hasErrorStatus = betslipStatus === App.BetslipStatus.ERROR;

    const { name: marketName, status: marketStatus } = market;

    const { status, disabled, betStop } = match;

    const { id: betslipBetId, mode, betslipOddIds } = betslipBet;
    const isSingle = mode === GQL.BetType.SINGLE;

    const betslipRestrictionsId = restrictionsModule?.utils.getBetslipRestrictionId(
        isSingle ? betslipOddId : betslipOddIds
    );

    const {
        id: oddId,
        value: oddValue,
        name: oddName,
        status: oddStatus,
        isActive: isActiveOdd,
    } = odd;

    const dispatch = useDispatch();

    const { minStake } = usePollingMinStake();
    const { state: restrictionsState } = useContext(RestrictionsContext);

    const freebetModule = useAsyncModule('freebet');

    const selectProps = useMemo(() => makeSelectProps(freebetModule), [
        freebetModule,
    ]);
    const {
        oddFormat,
        maxBet,
        restrictionErrors,
        isBetslipOddConflict,
        bet,
        isFreebet,
    } = useSelector((state: State) =>
        selectProps(state, {
            betslipOddId,
            betslipBetId,
            betslipRestrictionsId,
            oddId,
            betslipStatus,
            betId,
            restrictionsState,
        })
    );
    const { formatMessage } = useIntl();

    const maxStake = isFreebet ? '' : maxBet;

    const handleDeleteBetslipOdd = useCallback(() => {
        dispatch(
            deleteBetslipOdd({
                oddId,
                betslipBetId,
            })
        );
    }, [dispatch, oddId, betslipBetId]);

    const clickOddIconHandler = useCallback(() => {
        if (!isEditable) return;

        handleDeleteBetslipOdd();
    }, [isEditable, handleDeleteBetslipOdd]);

    const isOddActive = checkIsBetslipOddActive(
        status,
        disabled,
        marketStatus,
        oddStatus,
        isActiveOdd,
        betStop
    );

    const isLocked = !isOddActive && isEditable;

    const oddValueOrTicketCoef = (!isEditable && fixedCoef) || oddValue;

    const { errorMsgs, hasError } = getErrorMsgsAndStatus({
        betslipOdd,
        hasErrorStatus,
        isBetslipOddConflict,
        isSingle,
        isLast,
        isFreebet,
        maxStake,
        errorMessages: getRestrictionMessages(restrictionErrors, formatMessage),
        formatMessage,
        isLocked,
        declineReason: bet?.declineReason,
        minStake,
    });

    return {
        clickOddIconHandler,
        isLocked,
        marketName,
        oddName,
        oddValue: oddValueOrTicketCoef,
        hasError,
        errorMsgs,
        oddFormat,
    };
};

const selectBetslipStatusFromProps = (
    _: State,
    { betslipStatus }: { betslipStatus: App.BetslipStatus }
) => betslipStatus;

const selectRestrictions = createSelector(
    selectFilteredRestrictionsByOddId,
    selectBetslipStatusFromProps,
    (restrictions, betslipStatus) => {
        const { maxBetRestriction, simpleRestrictions } = normalizeRestriction(
            restrictions
        );

        const blackList = getRestrictionBlackList(betslipStatus);

        const restrictionErrors = simpleRestrictions.filter(
            r => !includes(blackList, r.type)
        );

        return {
            maxBet: maxBetRestriction?.maxBet.toString() ?? '',
            restrictionErrors,
        };
    }
);

const makeSelectProps = (freebetModule: Modules['freebet']['default'] | null) =>
    createSelector(
        selectIsBetslipOddConflict,
        selectRestrictions,
        selectOddFormat,
        selectCachedBet,
        makeSelectIsFreebet(freebetModule),
        (isBetslipOddConflict, restrictions, oddFormat, bet, isFreebet) => {
            const { maxBet, restrictionErrors } = restrictions || {
                maxBet: '',
                restrictionErrors: [],
            };

            return {
                isBetslipOddConflict,
                maxBet,
                restrictionErrors,
                oddFormat,
                bet,
                isFreebet,
            };
        }
    );

interface Params {
    isLast: boolean;
    hasErrorStatus: boolean;
    isBetslipOddConflict: boolean;
    isSingle: boolean;
    isFreebet: boolean;
    maxStake: string;
    errorMessages: Array<string | React.ReactNode>;
    formatMessage: ReturnType<typeof useIntl>['formatMessage'];
    betslipOdd: ValidOddProps['betslipOdd'];
    isLocked: boolean;
    declineReason: string | undefined;
    minStake: number;
}

function getRestrictionMessages(
    restrictionErrors: View.Restriction[],
    formatMessage: ReturnType<typeof useIntl>['formatMessage']
) {
    return restrictionErrors.map(restriction => {
        const { descriptor, values } = getRestrictionMessage(restriction);

        return formatMessage(descriptor, values);
    });
}

function getErrorMsgsAndStatus({
    betslipOdd,
    hasErrorStatus,
    isBetslipOddConflict,
    isSingle,
    isLast,
    isFreebet,
    maxStake,
    errorMessages,
    formatMessage,
    isLocked,
    declineReason,
    minStake,
}: Params) {
    if (!includes(BETSLIP_ODD_STATUSES_TO_PLACE_BET, betslipOdd.singleStatus)) {
        return {
            errorMsgs: [],
            hasError: false,
        };
    }

    return hasErrorStatus && !!errorMessages.length
        ? {
              errorMsgs: errorMessages,
              hasError: hasErrorStatus,
          }
        : getErrorsFromStakeValue({
              betslipOdd,
              hasErrorStatus,
              isBetslipOddConflict,
              isSingle,
              isLast,
              isFreebet,
              maxStake,
              errorMessages,
              formatMessage,
              isLocked,
              declineReason,
              minStake,
          });
}

function getErrorsFromStakeValue({
    isLast,
    betslipOdd: { singleStake },
    hasErrorStatus,
    isBetslipOddConflict,
    isSingle,
    isFreebet,
    maxStake,
    errorMessages,
    formatMessage,
    isLocked,
    declineReason,
    minStake,
}: Params) {
    const errorMsgs: Array<string | string[]> = [];

    const isMaxStakeError =
        isSingle && maxStake !== '' && +singleStake > +maxStake;

    const isMinStakeError =
        isSingle &&
        !isFreebet &&
        !!+singleStake &&
        !!minStake &&
        +singleStake < minStake;

    const isMaxLessMinStakeError =
        isSingle && !!minStake && maxStake !== '' && +maxStake < minStake;

    const hasError =
        hasErrorStatus ||
        isBetslipOddConflict ||
        isMaxStakeError ||
        isMinStakeError ||
        isMaxLessMinStakeError ||
        isLocked;

    if (hasErrorStatus && isSingle) {
        errorMsgs.push([
            `${formatMessage(messages.attention)}!`,
            getErrorMsgByResponse(
                declineReason as Betslip.ErrorCodes | undefined,
                bindTrailingArgs(formatMessage, {
                    host: window.location.host,
                }),
                messages as Arg3<typeof getErrorMsgByResponse>
            ),
        ]);
    }

    if (!isMaxLessMinStakeError) {
        if (isMinStakeError) {
            errorMsgs.push(
                `${formatMessage(messages.stakeMinimalStakeIs)} ${minStake}`
            );
        }

        if (isMaxStakeError) {
            errorMsgs.push(
                `${formatMessage(messages.errorMaxStake)} ${convertViewMaxStake(
                    maxStake
                )}`
            );
        }
    }

    if (!isFreebet) {
        errorMessages.forEach(error => {
            errorMsgs.push([
                `${formatMessage(messages.attention)}!`,
                `${error}`,
            ]);
        });
    }

    if (isBetslipOddConflict && isLast) {
        errorMsgs.push([
            `${formatMessage(messages.attention)}!`,
            formatMessage(messages.betslipOddsHasConflicts),
        ]);
    }

    if (isMaxLessMinStakeError) {
        errorMsgs.push([
            `${formatMessage(messages.attention)}!`,
            formatMessage(messages.maxBet),
        ]);
    }

    if (isLocked) {
        errorMsgs.push([
            `${formatMessage(messages.attention)}!`,
            formatMessage(messages.oddStatus),
        ]);
    }

    return { errorMsgs, hasError };
}

export default useOdd;
